import React, { useRef, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { RecoveryCodesStep } from "./RecoveryCodesStep";
import { useRegenerateRecoveryCodes } from "../../hooks/useRegenerateRecoveryCodes";

export interface RegenerateCodesModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const RegenerateCodesModal = ({ isOpen, onClose }: RegenerateCodesModalProps) => {
	const [password, setPassword] = useState("");
	const [newCodes, setNewCodes] = useState<string[] | null>(null);
	const regenerate = useRegenerateRecoveryCodes();

	// PRO-731 follow-up — land focus on the password box instead of the modal's
	// close button (Chakra's focus lock otherwise focuses the first tabbable
	// element). Same mechanism as ScoringModal; this modal owns its own Modal and
	// the field is mounted at open time, so `initialFocusRef` is all that's needed.
	const passwordRef = useRef<HTMLInputElement>(null);

	const handleClose = () => {
		setPassword("");
		setNewCodes(null);
		regenerate.reset();
		onClose();
	};

	const handleConfirm = () => {
		regenerate.mutate(
			{ password },
			{
				onSuccess: (resource) => {
					setNewCodes(resource.recovery_codes);
				},
			}
		);
	};

	// PRO-731 follow-up (item 5) — this password field isn't inside a `<form>`
	// (the primary action lives in the shared Modal's `primaryButton`), so
	// there is nothing for the browser to submit on Enter. preventDefault +
	// the same guard as the button itself (`password`/`isPending`) stops a
	// double-submit while a request is already in flight.
	const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") {
			return;
		}
		e.preventDefault();
		if (password && !regenerate.isPending) {
			handleConfirm();
		}
	};

	if (newCodes) {
		return (
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title="New recovery codes"
				iconType="download"
				closeOnEsc
			>
				<RecoveryCodesStep codes={newCodes} onDone={handleClose} />
			</Modal>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			initialFocusRef={passwordRef}
			title="Regenerate recovery codes"
			description="Your existing recovery codes will stop working. Confirm your password to continue."
			iconType="warning"
			modalType="alert"
			closeOnEsc
			primaryButton={{
				text: "Regenerate",
				btntype: "danger",
				disabled: !password,
				isLoading: regenerate.isPending,
				onClick: handleConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: handleClose,
			}}
		>
			<InputWithLabel
				label="Password"
				type="password"
				icon
				inputRef={passwordRef}
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				onKeyDown={handlePasswordKeyDown}
				errorText={regenerate.error?.message}
				testId="regenerate-codes-password-input"
				required
			/>
		</Modal>
	);
};
